import { useGmbGoals } from '../hooks/useGmbStrategy';
import type { GmbGoal } from '../types';

interface GoalSettingProps {
  onTooltipShow: (title: string, content: string) => void;
}

// Google exposes no WhatsApp or chat message counts (Business Messages was
// retired), so these goals are kept per the BRD but can't be measured.
const UNTRACKED_KPIS = new Set(['whatsapp', 'messages']);

export default function GoalSetting({ onTooltipShow }: GoalSettingProps) {
  const { data: goalsData, isLoading, error, refetch } = useGmbGoals();

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <i className="ri-error-warning-line text-red-500 text-3xl mb-3 block"></i>
        <h3 className="text-sm font-semibold text-red-800">Failed to load goals</h3>
        <p className="text-xs text-red-600 mt-1">{error.message}</p>
        <button onClick={() => refetch()} className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700">
          Retry
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 animate-pulse">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32 mb-5"></div>
          {[1, 2, 3].map(i => (
            <div key={i} className="h-12 bg-gray-100 dark:bg-gray-700 rounded mb-2"></div>
          ))}
        </div>
      </div>
    );
  }

  const goals: GmbGoal[] = goalsData?.data?.goals || [];

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'active': return 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300';
      case 'paused': return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300';
      case 'completed': return 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300';
      case 'not_started': return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active': return 'On Track';
      case 'paused': return 'At Risk';
      case 'completed': return 'Achieved';
      case 'not_started': return 'Not Started';
      default: return status;
    }
  };

  return (
    <div className="space-y-6">
      {/* Disclaimer Banner */}
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 flex items-start gap-3">
        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/40 shrink-0">
          <i className="ri-focus-3-line text-amber-600 dark:text-amber-400 text-lg"></i>
        </div>
        <div>
          <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">Experimental Goals</p>
          <p className="text-sm text-amber-700 dark:text-amber-300 mt-0.5">Benchmarks for 3 months. Realistic targets will be set after evaluation.</p>
        </div>
      </div>

      {/* Goals Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">Goals & KPIs</h3>
          <button
            onClick={() => onTooltipShow('Goal Setting', 'Define measurable targets for your GMB performance. Each goal includes a target, KPI metric, and status tracker.')}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            title="More info"
          >
            <i className="ri-information-line text-lg"></i>
          </button>
        </div>

        {goals.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <i className="ri-flag-line text-4xl text-gray-300 dark:text-gray-600 mb-3 block"></i>
            <p className="text-sm">No goals defined yet. Run strategy generation to create initial goals.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide py-3 px-3">Goal Type</th>
                  <th className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide py-3 px-3">Target</th>
                  <th className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide py-3 px-3">Baseline / KPI</th>
                  <th className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide py-3 px-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {goals.map((goal) => (
                  <tr key={goal.goal_id} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td className="py-3 px-3">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {goal.goal_type?.split('-').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                      </span>
                      {UNTRACKED_KPIS.has(goal.kpi_type) && (
                        <p className="mt-1 max-w-xs text-[11px] leading-snug text-amber-600 dark:text-amber-400">
                          Tracking not available from Google. Google doesn't share {goal.kpi_type === 'whatsapp' ? 'WhatsApp' : 'chat'} message data with third-party tools.
                        </p>
                      )}
                      {goal.kpi_type === 'review_count' && (
                        <p className="mt-1 max-w-xs text-[11px] leading-snug text-gray-400 dark:text-gray-500">
                          Reviews depend on your customers, so they may not move in direct step with the action plan.
                        </p>
                      )}
                    </td>
                    <td className="py-3 px-3">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{goal.target || `${goal.target_value}`}</span>
                    </td>
                    <td className="py-3 px-3">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {UNTRACKED_KPIS.has(goal.kpi_type) ? '—' : goal.current_value ?? 0}
                        <span className="text-xs text-gray-400 dark:text-gray-500"> / {goal.kpi || '—'}</span>
                      </span>
                    </td>
                    <td className="py-3 px-3">
                      {UNTRACKED_KPIS.has(goal.kpi_type) ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                          <i className="ri-eye-off-line"></i>
                          Not trackable
                        </span>
                      ) : (
                        <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${getStatusClass(goal.status)}`}>
                          <i className={`${goal.status === 'active' ? 'ri-checkbox-circle-line' : goal.status === 'completed' ? 'ri-checkbox-circle-line' : goal.status === 'paused' ? 'ri-error-warning-line' : 'ri-time-line'}`}></i>
                          {getStatusLabel(goal.status)}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
